import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useDropzone } from "react-dropzone";
import BeatLoader from "react-spinners/BeatLoader";
import { trpc } from "../../utils/trpc";
import { Constants } from "../../../../../common/constants";
import { ErrorNote } from "../../forms/improved_components/error_note";
import { SeshPrimaryButton } from "../../forms/improved_components/base_components/sesh_primary_button";
import { SettingsBooleanItem } from "../../forms/improved_components/settings_items/settings_boolean_item";
import { BoxItem } from "../../forms/improved_components/box_item";
import { DashboardUtils } from "../../dashboard_utils";

interface CustomizeSettingsProps {
	is_admin: boolean;
}

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB

export function CustomizeSettings({ is_admin }: CustomizeSettingsProps) {
	const { server_id } = useParams<{ server_id: string }>();

	const customization_query = trpc.guild_customize.getCustomization.useQuery(
		{ guild_id: server_id! },
		{ enabled: !!server_id }
	);

	const presigned_url_mutation =
		trpc.guild_customize.getPresignedUploadUrl.useMutation();
	const update_mutation =
		trpc.guild_customize.updateCustomization.useMutation();
	const reset_mutation = trpc.guild_customize.resetCustomization.useMutation();

	const [customBotName, setCustomBotName] = useState<string>("");
	const [pendingAvatarUrl, setPendingAvatarUrl] = useState<string | null>(null);
	const [pendingFile, setPendingFile] = useState<File | null>(null);
	const [useCustomLogoOnDashboard, setUseCustomLogoOnDashboard] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);

	// Initialize form with current values
	useEffect(() => {
		if (customization_query.data) {
			setCustomBotName(customization_query.data.custom_bot_name || "");
			setUseCustomLogoOnDashboard(customization_query.data.use_custom_logo_on_dashboard || false);
		}
	}, [customization_query.data]);

	const onDrop = useCallback(
		async (acceptedFiles: File[]) => {
			if (acceptedFiles.length === 0) return;

			const file = acceptedFiles[0];
			setUploadError(null);

			// Validate file type
			if (!ALLOWED_TYPES.includes(file.type)) {
				setUploadError(
					"Invalid file type. Please use PNG, JPG, GIF, or WebP."
				);
				return;
			}

			// Validate file size
			if (file.size > MAX_FILE_SIZE) {
				setUploadError("File too large. Maximum size is 8MB.");
				return;
			}

			setPendingFile(file);
			setIsUploading(true);

			try {
				// Get presigned URL
				const result = await presigned_url_mutation.mutateAsync({
					guild_id: server_id!,
					filename: file.name,
					content_type: file.type,
				});

				// Upload directly to B2
				const uploadResponse = await fetch(result.upload_url, {
					method: "PUT",
					headers: {
						"Content-Type": file.type,
					},
					body: file,
				});

				if (!uploadResponse.ok) {
					throw new Error("Upload failed");
				}

				setPendingAvatarUrl(result.final_url);
			} catch (err: any) {
				setUploadError(err.message || "Failed to upload image");
				setPendingFile(null);
			} finally {
				setIsUploading(false);
			}
		},
		[server_id, presigned_url_mutation]
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			"image/png": [".png"],
			"image/jpeg": [".jpg", ".jpeg"],
			"image/gif": [".gif"],
			"image/webp": [".webp"],
		},
		maxFiles: 1,
		maxSize: MAX_FILE_SIZE,
		disabled: isUploading || isSaving,
	});

	const handleSave = async () => {
		if (!server_id) return;

		setIsSaving(true);

		try {
			await update_mutation.mutateAsync({
				guild_id: server_id,
				custom_bot_name: customBotName || null,
				custom_bot_avatar_url:
					pendingAvatarUrl !== null ? pendingAvatarUrl : undefined,
				use_custom_logo_on_dashboard: useCustomLogoOnDashboard,
			});

			customization_query.refetch();
			setPendingFile(null);
			setPendingAvatarUrl(null);
		} finally {
			setIsSaving(false);
		}
	};

	const handleResetName = async () => {
		if (!server_id) return;

		setIsSaving(true);

		try {
			await reset_mutation.mutateAsync({
				guild_id: server_id,
				reset_name: true,
			});

			setCustomBotName("");
			customization_query.refetch();
		} finally {
			setIsSaving(false);
		}
	};

	const handleResetAvatar = async () => {
		if (!server_id) return;

		setIsSaving(true);

		try {
			await reset_mutation.mutateAsync({
				guild_id: server_id,
				reset_avatar: true,
			});

			setPendingFile(null);
			setPendingAvatarUrl(null);
			customization_query.refetch();
		} finally {
			setIsSaving(false);
		}
	};

	const handleClearPendingAvatar = () => {
		setPendingFile(null);
		setPendingAvatarUrl(null);
	};

	// Loading state
	if (customization_query.isLoading) {
		return <>{DashboardUtils.RenderLoadingThrobber()}</>;
	}

	// Upgrade prompt for non-custom-tier guilds
	if (!customization_query.data?.has_custom_tier) {
		return (
			<div className="has-text-centered py-6">
				<p className="mb-4">
					Set a custom name and avatar for{" "}
					<span className="sesh-text">sesh</span> in your server. This
					feature creates a white-label experience where the bot appears
					with your own branding.
				</p>
				<div
					className="rounded-xl p-6"
					style={{
						maxWidth: "400px",
						margin: "0 auto",
						background: "linear-gradient(to bottom right, rgba(8, 145, 178, 0.2), rgba(30, 41, 59, 0.8))",
						border: "1px solid rgba(0, 244, 255, 0.3)",
					}}
				>
					<p className="mb-4 text-content-primary">
						<i className="fas fa-crown mr-2 text-sesh-primary"></i>
						This feature requires{" "}
						<strong className="text-sesh-primary">sesh Pro</strong>.
					</p>
					<a
						href="/dashboard/premium"
						className="inline-flex items-center gap-2 rounded-lg bg-sesh-primary px-4 py-2 font-semibold text-base-dark transition-all hover:bg-cyan-300"
					>
						<i className="fas fa-arrow-right"></i>
						View Premium Options
					</a>
				</div>
			</div>
		);
	}

	// Permission check
	if (!is_admin) {
		return (
			<ErrorNote error="You need Manage Server permissions to customize the bot." />
		);
	}

	const currentAvatarUrl =
		pendingAvatarUrl || customization_query.data?.custom_bot_avatar_url;

	return (
		<div className="flex flex-col gap-2">
			{/* Bot Name Section */}
			<BoxItem bgColor="semidark" className="flex-col gap-4">
				<div className="flex flex-col gap-2">
					<div className="font-bold md:text-lg">Bot Name</div>
					<div className="text-sm text-content-secondary">
						Set a custom nickname for <span className="sesh-text">sesh</span> in this server. Leave empty to use the default name.
					</div>
				</div>

				<div className="flex flex-col gap-2">
					<input
						type="text"
						className="input"
						placeholder="sesh"
						maxLength={32}
						value={customBotName}
						onChange={(e) => setCustomBotName(e.target.value)}
						disabled={isSaving}
					/>
					<div className="flex items-center justify-between">
						<span className="text-xs text-content-secondary">
							{customBotName.length}/32 characters
						</span>
						{customization_query.data?.custom_bot_name && (
							<SeshPrimaryButton
								custom_size="small"
								onClick={handleResetName}
								disabled={isSaving}
							>
								<i className="fas fa-undo mr-1"></i>
								Reset to Default
							</SeshPrimaryButton>
						)}
					</div>
				</div>
			</BoxItem>

			{/* Bot Avatar Section */}
			<BoxItem bgColor="semidark" className="flex-col gap-4">
				<div className="flex flex-col gap-2">
					<div className="font-bold md:text-lg">Bot Avatar</div>
					<div className="text-sm text-content-secondary">
						Upload a custom avatar for <span className="sesh-text">sesh</span> in this server. Supported formats: PNG, JPG, GIF, WebP. Max size: 8MB.
					</div>
				</div>

				{uploadError && (
					<div className="notification is-danger">
						<button
							className="delete"
							onClick={() => setUploadError(null)}
						></button>
						{uploadError}
					</div>
				)}

				{currentAvatarUrl && (
					<div className="flex flex-col gap-2" key={pendingAvatarUrl ? "pending" : "current"}>
						<span className="text-sm font-semibold text-content-secondary">
							{pendingAvatarUrl ? "New Avatar:" : "Current Avatar:"}
						</span>
						<div className="flex items-center gap-3">
							<img
								src={currentAvatarUrl}
								alt="Bot avatar"
								className="h-20 w-20 rounded-full object-cover"
							/>
							{pendingAvatarUrl && (
								<SeshPrimaryButton
									custom_size="small"
									onClick={handleClearPendingAvatar}
									disabled={isSaving}
								>
									<i className="fas fa-times mr-1"></i>
									Cancel
								</SeshPrimaryButton>
							)}
						</div>
					</div>
				)}

				<div
					{...getRootProps()}
					className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-6 transition-all ${
						isDragActive
							? "border-sesh-primary bg-sesh-primary/10"
							: "border-gray-600 hover:border-gray-500"
					} ${isUploading ? "cursor-wait opacity-50" : ""}`}
				>
					<input {...getInputProps()} />
					{isUploading ? (
						<div key="uploading" className="flex flex-col items-center gap-2">
							<BeatLoader color={Constants.EMBED_COLOR} size={10} />
							<span className="text-sm">Uploading...</span>
						</div>
					) : isDragActive ? (
						<div key="dragging" className="flex flex-col items-center gap-2">
							<i className="fas fa-cloud-upload-alt text-2xl text-sesh-primary"></i>
							<span className="text-sm">Drop the image here...</span>
						</div>
					) : (
						<div key="idle" className="flex flex-col items-center gap-2">
							<i className="fas fa-cloud-upload-alt text-2xl text-content-secondary"></i>
							<span className="text-sm text-center">
								Drag and drop an image here, or click to select
							</span>
							<span className="text-xs text-content-secondary">
								PNG, JPG, GIF, or WebP. Max 8MB.
							</span>
						</div>
					)}
				</div>

				{customization_query.data?.custom_bot_avatar_url &&
					!pendingAvatarUrl && (
						<SeshPrimaryButton
							custom_size="small"
							className="w-fit"
							onClick={handleResetAvatar}
							disabled={isSaving}
						>
							<i className="fas fa-undo mr-1"></i>
							Reset to Default
						</SeshPrimaryButton>
					)}
			</BoxItem>

			{/* Dashboard Branding Section */}
			<SettingsBooleanItem
				label="Dashboard Branding"
				description={
					<>
						Replace the <span className="sesh-text">sesh</span> logo in the dashboard navbar with your custom avatar when viewing this server's pages.
						{!currentAvatarUrl && (
							<span className="mt-1 block text-amber-400">
								<i className="fas fa-info-circle mr-1"></i>
								Upload a custom avatar above to enable this feature.
							</span>
						)}
					</>
				}
				value={useCustomLogoOnDashboard}
				is_loading={false}
				disabled={isSaving || !currentAvatarUrl}
				onChange={setUseCustomLogoOnDashboard}
			/>

			{/* Save Button */}
			<div className="mt-2">
				<SeshPrimaryButton
					onClick={handleSave}
					disabled={isSaving || isUploading}
					is_loading={isSaving}
					className="w-fit"
				>
					<i className="fas fa-save mr-2"></i>
					Save Changes
				</SeshPrimaryButton>
			</div>
		</div>
	);
}
